import * as React from 'react'
import { capitalize } from 'lodash'
import { useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { TableHead } from '@mui/material'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { CourseUserEnrollDetailState } from '../../../../features/types'
import AddStudentCourseDialog from './AddStudentCourseDialog'
import { DEFAULT_PAGE_SIZE } from '../../../../utils/constants'

interface Props {
    studentId: number
    courses: CourseUserEnrollDetailState[]
    isFetchingCourses: boolean
    onDelete?: (id: number) => void
}

const StudentCoursesTable: React.FC<Props> = ({
    studentId,
    courses = [],
    isFetchingCourses = false,
    onDelete,
}) => {
    const { formatMessage, locale } = useIntl()
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_SIZE)

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - courses?.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            {courses ? (
                <TableContainer
                    component={Paper}
                    sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                    elevation={0}
                >
                    <Table sx={{ minWidth: 500 }}>
                        <TableHead sx={{ bgcolor: 'grey.100' }}>
                            <TableRow>
                                <TableCell>
                                    {formatMessage({ id: 'course' })}
                                </TableCell>

                                <TableCell>
                                    {formatMessage({ id: 'language' })}
                                </TableCell>

                                <TableCell>
                                    {formatMessage({ id: 'subjects' })}
                                </TableCell>

                                <TableCell>
                                    {formatMessage({ id: 'startDate' })}
                                </TableCell>

                                <TableCell>
                                    {formatMessage({ id: 'endDate' })}
                                </TableCell>

                                <TableCell>
                                    {formatMessage({ id: 'group' })}
                                </TableCell>

                                <TableCell align="right">
                                    {formatMessage({ id: 'edit' })}
                                </TableCell>

                                <TableCell align="right">
                                    {formatMessage({ id: 'delete' })}
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        {courses.length ? (
                            <TableBody>
                                {(rowsPerPage > 0
                                    ? courses.slice(
                                          page * rowsPerPage,
                                          page * rowsPerPage + rowsPerPage,
                                      )
                                    : courses
                                ).map((row) => (
                                    <TableRow key={row.courseId}>
                                        <TableCell
                                            component="th"
                                            scope="row"
                                        >
                                            {row[`title${capitalize(locale)}`]}
                                        </TableCell>

                                        <TableCell
                                            component="th"
                                            scope="row"
                                        >
                                            {row.language}
                                        </TableCell>

                                        <TableCell
                                            component="th"
                                            scope="row"
                                        >
                                            {
                                                row[
                                                    `subjectsTitle${capitalize(
                                                        locale,
                                                    )}`
                                                ]
                                            }
                                        </TableCell>

                                        <TableCell
                                            component="th"
                                            scope="row"
                                        >
                                            {row.startDate.toString()}
                                        </TableCell>

                                        <TableCell
                                            component="th"
                                            scope="row"
                                        >
                                            {row.endDate.toString()}
                                        </TableCell>

                                        <TableCell
                                            component="th"
                                            scope="row"
                                        >
                                            {row.groupTitle}
                                        </TableCell>

                                        <TableCell
                                            style={{ width: 60 }}
                                            align="right"
                                        >
                                            <AddStudentCourseDialog
                                                studentId={studentId}
                                                editCourse={row}
                                                editMode={true}
                                            />
                                        </TableCell>

                                        <TableCell
                                            style={{ width: 60 }}
                                            align="right"
                                        >
                                            <ConfirmDelete
                                                disabled={Boolean(
                                                    row.coursePriceId ||
                                                        row.promocodeId,
                                                )}
                                                confirmAction={() =>
                                                    onDelete(row.id)
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {emptyRows > 0 && (
                                    <TableRow
                                        style={{ height: 53 * emptyRows }}
                                    >
                                        <TableCell colSpan={10} />
                                    </TableRow>
                                )}
                            </TableBody>
                        ) : (
                            !isFetchingCourses && (
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            align="center"
                                            colSpan={10}
                                        >
                                            {formatMessage({ id: 'noData' })}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            )
                        )}

                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[
                                        5,
                                        10,
                                        25,
                                        { label: 'All', value: -1 },
                                    ]}
                                    colSpan={10}
                                    count={courses.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    SelectProps={{
                                        inputProps: {
                                            'aria-label': 'rows per page',
                                        },
                                        native: true,
                                    }}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={
                                        handleChangeRowsPerPage
                                    }
                                    ActionsComponent={TablePaginationActions}
                                    sx={{ border: 0 }}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            ) : (
                <Box sx={{ p: 1 }}>{formatMessage({ id: 'noData' })}</Box>
            )}
        </>
    )
}

export default StudentCoursesTable
